import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import CountryFlag from '../common/CountryFlag';
import './Leagues.css';

/**
 * Searchable multi-select of registered guild players for building a roster.
 *
 * Unlike the tournaments PlayerPicker (keyed on Discord id, inline-styled), this
 * works in ``player_id`` terms — the identifier the league API expects — and is
 * styled with CSS classes. Only players with a linked account (``player_id``)
 * are selectable.
 *
 * @param {object} props - Component props.
 * @param {string} props.guildId - Guild whose players to load.
 * @param {Array<object>} props.selected - Currently selected player objects.
 * @param {Function} props.onToggle - Called with a player object to add/remove it.
 * @param {number} [props.maxPlayers] - Optional cap on selections.
 * @param {Array<string>} [props.excludeIds] - Player ids to hide (already on the roster).
 */
export default function LeaguePlayerPicker({ guildId, selected, onToggle, maxPlayers, excludeIds }) {
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!guildId) return;
    setLoading(true);
    fetch(`${API_URL}/api/web/players/${guildId}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        // The guild-players endpoint returns flat rows (id, steam_username,
        // discord_username, avatar, steam_id, elo). Normalise to the shape the
        // picker and its consumers expect (player_id + nested steam).
        const rows = (Array.isArray(data) ? data : [])
          .filter((p) => p.id)
          .map((p) => ({
            player_id: p.id,
            username: p.discord_username || p.steam_username || p.id,
            steam: { username: p.steam_username, avatar: p.avatar },
            steam_id: p.steam_id,
            country: p.country,
            elo: p.elo,
          }));
        setPlayers(rows);
      })
      .catch(() => setPlayers([]))
      .finally(() => setLoading(false));
  }, [guildId]);

  const selectedIds = useMemo(() => new Set(selected.map((p) => p.player_id)), [selected]);
  const atMax = maxPlayers && selected.length >= maxPlayers;

  const name = (p) => p.steam?.username || p.username || p.player_id;
  const avatar = (p) => p.steam?.avatar || p.avatar || null;

  const excludeSet = useMemo(() => new Set(excludeIds || []), [excludeIds]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return players
      .filter((p) => !excludeSet.has(p.player_id))
      .filter((p) => {
        if (!q) return true;
        return (
          (p.steam?.username || '').toLowerCase().includes(q) ||
          (p.username || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.elo?.elo || 0) - (a.elo?.elo || 0));
  }, [players, search, excludeSet]);

  if (loading) {
    return <div className="picker-loading">{t('profile.loadingPlayers')}</div>;
  }

  return (
    <div className="league-picker">
      <input
        type="text"
        className="picker-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('players.searchPlaceholder')}
      />
      <div className="picker-list">
        {filtered.length === 0 ? (
          <div className="picker-empty">{search ? t('palette.noResults') : t('leagues.noPlayers')}</div>
        ) : (
          filtered.map((p) => {
            const isSel = selectedIds.has(p.player_id);
            const disabled = atMax && !isSel;
            return (
              <button
                key={p.player_id}
                type="button"
                disabled={disabled}
                className={`picker-row ${isSel ? 'is-selected' : ''}`}
                onClick={() => onToggle(p)}
              >
                {avatar(p) ? (
                  <img className="picker-avatar" src={avatar(p)} alt="" />
                ) : (
                  <span className="picker-avatar picker-avatar-blank" />
                )}
                {p.country && <CountryFlag code={p.country} size="sm" className="picker-flag" />}
                <span className="picker-name">{name(p)}</span>
                {p.elo && <span className="picker-elo">{p.elo.elo} ELO</span>}
                <span className="picker-check">{isSel ? '✓' : ''}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
